import React from "react";
import { ChevronRight } from "lucide-react";
import { useRevealGroup } from "../../hooks/useReveal";
import { useViewRouter } from "../../hooks/useViewRouter";

/**
 * Section ④ — Four-pillar strip on a near-black canvas (#0A0A0A).
 *
 * All four cards are the SAME height (75vh, min 560px) — two equal rows
 * of 50/50 cards with 4px gutters. Row 1 holds the live flagship cards
 * (Capital Office + Dealroom). Row 2 holds the Coming Soon cards
 * (Estates + Atelier) — same dimensions, only the image is softly
 * blurred and a gold "COMING SOON" badge sits top-left.
 *
 * Card text styling is uniform across all four cards: Cormorant titles
 * (42px, upright, white), Cormorant sublines (18px, upright, white —
 * NOT italic, NOT gold), Jost 12px bullets and 11px CTAs. Gold is
 * reserved for the eyebrow accent, bullet dots, CTA arrow and the
 * Coming Soon badge only.
 */
const ROW1 = [
  {
    eyebrow: "I · WEALTH ADVISORY",
    title: "The Capital Office",
    subline: "Real Estate Intelligence, Structured for Wealth",
    bullets: [
      "Market heat maps & micro-market analysis",
      "AI-powered deal scoring & portfolio intelligence",
      "Entry/exit timing · Yield optimisation · NRI advisory",
    ],
    cta: "ENTER THE CAPITAL OFFICE",
    view: "capital",
    image: "/img/pillars/capital.jpg",
  },
  {
    eyebrow: "II · PROPERTY MARKETPLACE",
    title: "The Dealroom",
    subline: "Strategic Inventory Across Finest Asset Classes",
    pills: ["RESIDENTIAL", "COMMERCIAL", "LAND", "FARMHOUSE", "PLOTTED"],
    bullets: [
      "Pre-vetted ultra-luxury inventory across Delhi NCR",
      "Off-market mandates — never listed publicly",
      "Full financial transparency: rate, yield, growth potential",
    ],
    cta: "ENTER THE DEALROOM",
    view: "dealroom",
    image: "/img/pillars/dealroom.jpg",
  },
];

const ROW2 = [
  {
    eyebrow: "III · BESPOKE EXPERIENCES",
    title: "The Estates",
    subline: "Curated Properties Across Exceptional Addresses",
    description:
      "Handpicked luxury retreats across India and internationally. Private stays · Bespoke events · Chauffeur · Chef · Butler",
    cta: "JOIN THE WAITLIST",
    view: "estates",
    image: "/img/pillars/estates.jpg",
  },
  {
    eyebrow: "IV · CRAFT & BUILD",
    title: "The Atelier",
    subline: "Architecture, Interiors & Living",
    description:
      "Bespoke interiors with internationally inspired luxury. Construction: Victorian · Neo-Classical · Indian-Heritage · Modern",
    cta: "REGISTER INTEREST",
    view: "atelier",
    image: "/img/pillars/atelier.jpg",
  },
];

const CARD_GRADIENT =
  "linear-gradient(to bottom, transparent 0%, transparent 25%, rgba(8,8,8,0.60) 55%, rgba(8,8,8,0.92) 100%)";

const ComingSoonBadge = () => (
  <span
    className="absolute uppercase"
    style={{
      top: 24,
      left: 24,
      zIndex: 3,
      fontFamily: "'Jost', sans-serif",
      fontWeight: 400,
      fontSize: 9,
      letterSpacing: "0.40em",
      padding: "7px 16px",
      border: "1px solid #C8A95A",
      color: "#C8A95A",
      backgroundColor: "rgba(200,169,90,0.12)",
    }}
  >
    COMING SOON
  </span>
);

const CardEyebrow = ({ children }) => (
  <div
    className="uppercase"
    style={{
      fontFamily: "'Jost', sans-serif",
      fontWeight: 400,
      fontSize: 10,
      letterSpacing: "0.44em",
      color: "rgba(255,255,255,0.60)",
      marginBottom: 18,
    }}
  >
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <div
    style={{
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 500,
      fontStyle: "normal",
      fontSize: 42,
      lineHeight: 1.1,
      color: "#FFFFFF",
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const CardSubline = ({ children }) => (
  <div
    style={{
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 400,
      fontStyle: "normal",
      fontSize: 18,
      lineHeight: 1.3,
      color: "rgba(255,255,255,0.75)",
      marginBottom: 18,
    }}
  >
    {children}
  </div>
);

const CardCTA = ({ label }) => (
  <div
    data-card-cta=""
    className="inline-flex items-center gap-2 uppercase"
    style={{
      fontFamily: "'Jost', sans-serif",
      fontWeight: 400,
      fontSize: 11,
      letterSpacing: "0.32em",
      color: "rgba(255,255,255,0.55)",
      marginTop: 20,
      transition: "color 400ms ease, transform 400ms ease",
    }}
  >
    {label}
    <ChevronRight size={14} strokeWidth={1.5} />
  </div>
);

const PrimaryCard = ({ p }) => {
  const { showView } = useViewRouter();
  return (
    <button
      type="button"
      onClick={() => showView(p.view)}
      className="reveal group relative overflow-hidden text-left cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-num-gold"
      style={{ height: "75vh", minHeight: 560, transform: "none" }}
      data-testid={`pillar-card-${p.view}`}
      data-view={p.view}
    >
      <img
        src={p.image}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          transition: "transform 800ms cubic-bezier(0.25,0.46,0.45,0.94)",
        }}
      />
      <style>{`
        [data-testid="pillar-card-${p.view}"]:hover img {
          transform: scale(1.05);
        }
        [data-testid="pillar-card-${p.view}"]:hover [data-card-cta] {
          color: #C8A95A;
          transform: translateX(4px);
        }
      `}</style>

      {/* Gradient overlay — generous breathing room above text */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: CARD_GRADIENT, zIndex: 1 }}
      />

      <div
        className="absolute"
        style={{
          left: 0,
          right: 0,
          bottom: 0,
          padding: "0 36px 44px",
          zIndex: 2,
        }}
      >
        <CardEyebrow>{p.eyebrow}</CardEyebrow>
        <CardTitle>{p.title}</CardTitle>
        <CardSubline>{p.subline}</CardSubline>

        {p.pills && (
          <div className="flex flex-wrap gap-2 mb-5">
            {p.pills.map((pill) => (
              <span
                key={pill}
                className="uppercase"
                style={{
                  fontFamily: "'Jost', sans-serif",
                  fontSize: 8,
                  letterSpacing: "0.28em",
                  padding: "4px 10px",
                  border: "1px solid rgba(200,169,90,0.55)",
                  color: "#C8A95A",
                }}
              >
                {pill}
              </span>
            ))}
          </div>
        )}

        <ul className="space-y-2">
          {p.bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-3"
              style={{
                fontFamily: "'Jost', sans-serif",
                fontSize: 12,
                color: "rgba(255,255,255,0.65)",
                lineHeight: 1.7,
              }}
            >
              <span
                className="block flex-shrink-0"
                style={{
                  width: 3,
                  height: 3,
                  marginTop: 8,
                  backgroundColor: "#C8A95A",
                  borderRadius: "50%",
                }}
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <CardCTA label={p.cta} />
      </div>
    </button>
  );
};

const ComingSoonCard = ({ p }) => {
  const { showView } = useViewRouter();
  return (
    <button
      type="button"
      onClick={() => showView(p.view)}
      className="reveal group relative overflow-hidden text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-num-gold"
      style={{
        height: "75vh",
        minHeight: 560,
        cursor: "pointer",
        transform: "none",
      }}
      data-testid={`pillar-card-${p.view}`}
      data-view={p.view}
    >
      <img
        src={p.image}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "blur(3px)", transform: "scale(1.05)" }}
      />
      <style>{`
        [data-testid="pillar-card-${p.view}"]:hover [data-card-cta] {
          color: #DFC07E;
          transform: translateX(4px);
        }
      `}</style>

      {/* Card gradient — same generous gradient as the primary cards */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(10,10,10,0.25) 0%, rgba(10,10,10,0.35) 25%, rgba(8,8,8,0.65) 55%, rgba(8,8,8,0.94) 100%)",
          zIndex: 1,
        }}
      />

      <ComingSoonBadge />

      <div
        className="absolute"
        style={{
          left: 0,
          right: 0,
          bottom: 0,
          padding: "0 36px 44px",
          zIndex: 2,
        }}
      >
        <CardEyebrow>{p.eyebrow}</CardEyebrow>
        <CardTitle>{p.title}</CardTitle>
        <CardSubline>{p.subline}</CardSubline>
        <p
          style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: 12,
            color: "rgba(255,255,255,0.65)",
            lineHeight: 1.7,
            maxWidth: 520,
          }}
        >
          {p.description}
        </p>
        {/* CTA — gold on Coming Soon cards */}
        <div
          data-card-cta=""
          className="inline-flex items-center gap-2 uppercase"
          style={{
            fontFamily: "'Jost', sans-serif",
            fontWeight: 400,
            fontSize: 11,
            letterSpacing: "0.32em",
            color: "#C8A95A",
            marginTop: 20,
            transition: "color 400ms ease, transform 400ms ease",
          }}
        >
          {p.cta}
          <ChevronRight size={14} strokeWidth={1.5} />
        </div>
      </div>
    </button>
  );
};

const PillarCardStrip = () => {
  const ref = useRevealGroup(".reveal");

  return (
    <section
      ref={ref}
      style={{ backgroundColor: "#0A0A0A" }}
      data-testid="pillar-strip-section"
    >
      {/* Header above cards */}
      <div className="text-center px-6" style={{ paddingTop: 70, paddingBottom: 56 }}>
        <div className="reveal">
          <span
            className="uppercase"
            style={{
              fontFamily: "'Jost', sans-serif",
              fontWeight: 400,
              fontSize: 9,
              letterSpacing: "0.48em",
              color: "#C8A95A",
            }}
          >
            FOUR PILLARS · ONE INSTITUTION
          </span>
        </div>
        <h2
          className="reveal delay-1 mt-7"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(34px, 4vw, 52px)",
            lineHeight: 1.1,
            color: "#FFFFFF",
          }}
        >
          A private real estate office,{" "}
          <span
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontWeight: 500,
              color: "#C8A95A",
            }}
          >
            reimagined.
          </span>
        </h2>
      </div>

      {/* Row 1 — Flagship cards */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2"
        style={{ gap: 4, backgroundColor: "#0A0A0A" }}
      >
        {ROW1.map((p) => (
          <PrimaryCard key={p.view} p={p} />
        ))}
      </div>

      {/* Row 2 — Coming Soon cards (same height as Row 1) */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2"
        style={{
          gap: 4,
          backgroundColor: "#0A0A0A",
          paddingTop: 4,
          paddingBottom: 80,
        }}
      >
        {ROW2.map((p) => (
          <ComingSoonCard key={p.view} p={p} />
        ))}
      </div>
    </section>
  );
};

export default PillarCardStrip;
